/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { useCallback } from 'react'
import { Platform } from 'react-native'
import Animated, { FadeIn, FadeOut, LinearTransition } from 'react-native-reanimated'
import styled from 'styled-components/native'

import { useHandleFocus } from 'libs/hooks/useHandleFocus'
import { useHandleHover } from 'libs/hooks/useHandleHover'
import { accessibleRadioProps } from 'shared/accessibilityProps/accessibleRadioProps'
import { TouchableOpacity } from 'ui/components/TouchableOpacity'
import { getBorderHoverStyle, getRadioColors } from 'ui/designSystem/RadioButton/helpers'
import { RadioCircle, RightBox } from 'ui/designSystem/RadioButton/RadioButtonDefault'
import { RadioDescriptionText } from 'ui/designSystem/RadioButton/styles/RadioButtonLabel.styles'
import { DetailedRadioProps, RadioStateObject } from 'ui/designSystem/RadioButton/types'
import { SelectableAsset } from 'ui/designSystem/SelectableAsset'
import { SelectableSizing } from 'ui/designSystem/types'

const isWeb = Platform.OS === 'web'
const layoutTransition = isWeb ? undefined : LinearTransition.duration(250)

export const RadioButtonDetailed = ({
  label,
  disabled,
  error,
  value,
  collapsed,
  description,
  asset,
  sizing,
  setValue,
  accessibilityLabel,
}: DetailedRadioProps) => {
  const selected = value === label
  const focusProps = useHandleFocus()
  const hoverProps = useHandleHover()
  const onToggle = useCallback(() => {
    setValue(selected ? '' : label)
  }, [selected, label, setValue])
  const radioState = {
    selected: error ? false : selected,
    error: error ?? false,
    disabled: disabled ?? false,
  }

  return (
    <AnimatedRadioWrapper layout={layoutTransition}>
      <RadioButtonContainer
        radioState={radioState}
        variant="detailed"
        sizing={sizing}
        collapsed={collapsed}
        isHover={hoverProps.isHover}>
        <TouchableContentContainer
          onPress={onToggle}
          {...accessibleRadioProps({ checked: selected, label, accessibilityLabel })}
          {...focusProps}
          {...hoverProps}>
          <RadioCircle radioState={radioState} variant="detailed" hoverProps={hoverProps} />
          <RightBox radioState={radioState} label={label} hoverProps={hoverProps}>
            {description ? (
              <RadioDescriptionText radioState={radioState} {...hoverProps}>
                {description}
              </RadioDescriptionText>
            ) : null}
          </RightBox>
          {asset ? (
            <LeftBox>
              <SelectableAsset {...asset} disable={disabled} />
            </LeftBox>
          ) : null}
        </TouchableContentContainer>
        {collapsed && selected ? (
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(150)}
            layout={layoutTransition}>
            <CollapsedContainer>{collapsed}</CollapsedContainer>
          </Animated.View>
        ) : null}
      </RadioButtonContainer>
    </AnimatedRadioWrapper>
  )
}

const AnimatedRadioWrapper = styled(Animated.View)({
  overflow: 'hidden',
})

const LeftBox = styled.View({ justifyContent: 'center' })

const CollapsedContainer = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.l,
}))

type ContainerProps = {
  radioState: RadioStateObject
  variant: 'default' | 'detailed'
  collapsed?: React.ReactNode
  sizing?: SelectableSizing
  isHover?: boolean
}

const TouchableContentContainer = styled(TouchableOpacity)(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  columnGap: theme.designSystem.size.spacing.m,
}))

const RadioButtonContainer = styled.View<ContainerProps>(
  ({ theme, radioState, sizing, variant, collapsed, isHover }) => {
    const { backgroundColor, borderColor } = getRadioColors(radioState, theme, {
      componentPart: 'container',
      variant,
      collapsed: !!collapsed,
    })
    return {
      width: sizing === 'fill' ? '100%' : undefined,
      alignSelf: sizing === 'hug' && isWeb ? 'flex-start' : undefined,
      backgroundColor,
      borderColor,
      borderWidth: 1,
      borderRadius: theme.designSystem.size.borderRadius.m,
      padding: theme.designSystem.size.spacing.l,
      ...getBorderHoverStyle({
        radioState,
        theme,
        isHover,
        radioStateBorderColorError: borderColor,
      }),
    }
  }
)
